import React, { useEffect, useState } from 'react';
import Footer from '../components/Footer';
import Header from '../components/Header';
import Swal from 'sweetalert2';
import { generateAxiosInstance } from '../hooks/useAuthorizedRequest';
import { generateAxiosInstanceInstitute } from '../hooks/useAuthorizedRequestForInstitute';
import { generateAxiosInstanceSection } from '../hooks/useAuthorizedRequestForSection';
import { generateAxiosInstanceCommon } from '../hooks/useAuthorizedReuestCommon';
import { useNavigate } from 'react-router-dom';
import { GeneratePdf } from '../components/GeneratePdf';
import { Loader } from '../components/ModalsCommonFunctions';
import { findIndexById, findPageNumber, deleteItem, SortIcon, searchArray } from '../components/Navigation';
import Pagination from '../components/Pagination';

const DeviceDetails = () => {
    const authorizedAxios = generateAxiosInstance();
    const authorizedAxiosInstitute = generateAxiosInstanceInstitute();
    const authorizedAxiosSection = generateAxiosInstanceSection();
    const authorizedAxiosCommon = generateAxiosInstanceCommon();
    const initialForm = {
        instituteMasterId: '',
        sectionMasterId: '',
        year: "",
        gradeId: "",
        divisionId: ""
    }
    const [instituteList, setInstituteList] = useState([]);
    const [sectionList, setSectionList] = useState([]);
    const [deviceData, setDeviceData] = useState([]);
    const [divisionList, setDivisionList] = useState([]);
    const [gradeList, setGradeList] = useState([]);
    const [yearList, setYearList] = useState([]);
    const [studentData, setStudentData] = useState(initialForm);

    const [startIndex, setStartIndex] = useState(null);
    const [currPage, setCurrPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [displayedData, setDisplayedData] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [saveId, setSaveId] = useState(null);

    const [searchValue, setSearchValue] = useState(null);
    const searchKeys = [
        "studentName",
        "academicYearMasterDetails.yearName",
        "instituteGradeMasterDetails.gradeName",
        "instituteDivisionMasterDetails.divisionName",
        "studentId",
        "deviceInfo.imei_no",
        "deviceInfo.serial_no",
        "deviceInfo.meta_info.os",
        "deviceInfo.meta_info.model",
        "deviceInfo.meta_info.version",
        "deviceInfo.meta_info.manufacturer",
    ];

    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const handleNavigate = (route) => {
        navigate(route);
    };

    useEffect(() => {
        getInstituteList();
    }, []);

    useEffect(() => {
        getDeviceData();
    }, [studentData]);

    const getInstituteList = () => {
        const apiUrlForInstituteList = `/get-institute-master`;
        authorizedAxios && authorizedAxios.get(apiUrlForInstituteList)
            .then(response => {
                setInstituteList(response.data.result);
            })
            .catch(error => {
                const errorMsg = error?.response?.data?.message || 'Something went wrong!';
                console.error('Error fetching data:', errorMsg);
            })
    }

    const getApiUrl = () => {
        let apiUrl = `/device-info?instituteMasterId=${studentData.instituteMasterId}&sectionMasterId=${studentData.sectionMasterId}&`;
        if (studentData.year !== '') {
            apiUrl += `year=${studentData.year}&`;
        }
        if (studentData.gradeId !== '') {
            apiUrl += `gradeId=${studentData.gradeId}&`;
        }
        if (studentData.divisionId !== '') {
            apiUrl += `divisionId=${studentData.divisionId}&`;
        }

        apiUrl = apiUrl.replace(/&$/, '');
        return apiUrl;
    }

    useEffect(() => {
        if (searchValue === '' && searchValue !== null) {
            setCurrPage(1);
            getDeviceData();
        }
    }, [searchValue]);

    const handleEmptySearch = (e) => {
        setSearchValue(e.target.value);
    }

    const handleSearch = async () => {
        if (searchValue !== null) {
            if (studentData.instituteMasterId.length !== 0 && studentData.sectionMasterId.length !== 0) {
                setCurrPage(1);
                setDeviceData(await searchArray("A", getApiUrl(), searchKeys, searchValue.trim()));
            }
        }
    }

    useEffect(() => {
        if (saveId) {
            const pageNumber = findPageNumber(deviceData, saveId, itemsPerPage);
            setCurrPage(pageNumber);
        }
    }, [saveId])

    useEffect(() => {
        setTotalPages(Math.ceil(deviceData.length / itemsPerPage));
        const startIndex = (currPage - 1) * itemsPerPage;
        let endIndex = startIndex + itemsPerPage;

        if (endIndex > deviceData.length) {
            endIndex = deviceData.length;
        }
        setStartIndex(startIndex);
        setDisplayedData(deviceData.slice(startIndex, endIndex));
    }, [deviceData, currPage, itemsPerPage]);

    const handleInsituteChange = (e) => {
        const { name, value } = e.target;
        setStudentData(prevData => {
            return {
                ...prevData,
                sectionMasterId: '',
                year: '',
                gradeId: '',
                divisionId: '',
                [name]: value
            }
        })

        setYearList([]);
        setGradeList([]);
        setDivisionList([]);

        const apiUrl = `/get-section?instituteMasterId=${value}`;
        authorizedAxiosInstitute && authorizedAxiosInstitute.get(apiUrl)
            .then(response => {
                setSectionList(response.data.result)
            })
            .catch(error => {
                const errorMsg = error?.response?.data?.message || 'Something went wrong!';
                console.error('Error fetching data:', errorMsg);
            });
    }

    const handleSectionChange = (e) => {
        const { name, value } = e.target;
        setStudentData((prevData) => {
            return {
                ...prevData,
                year: '',
                gradeId: '',
                divisionId: '',
                [name]: value
            }
        })

        setGradeList([]);
        setDivisionList([]);

        const apiUrl1 = `/sections-academic-years?sectionMasterId=${value}`;
        authorizedAxiosCommon && authorizedAxiosCommon.get(apiUrl1)
            .then(response => {
                setYearList(response.data.data)
            })
            .catch(error => {
                const errorMsg = error?.response?.data?.message || 'Something went wrong!';
                console.error('Error fetching data:', errorMsg);
            });
    }

    const handleYearChange = (e) => {
        const { name, value } = e.target;
        setStudentData((prevData) => {
            return {
                ...prevData,
                gradeId: '',
                divisionId: '',
                [name]: value
            }
        })

        setDivisionList([]);

        const apiUrl = `/get-institute-grade?instituteMasterId=${studentData.instituteMasterId}`;
        authorizedAxiosInstitute && authorizedAxiosInstitute.get(apiUrl)
            .then(response => {
                setGradeList(response.data.result)
            })
            .catch(error => {
                const errorMsg = error?.response?.data?.message || 'Something went wrong!';
                console.error('Error fetching data:', errorMsg);
            });
    }

    const getDeviceData = () => {
        if (studentData.instituteMasterId.length !== 0 && studentData.sectionMasterId.length !== 0) {
            authorizedAxios && authorizedAxios.get(getApiUrl())
                .then(response => {
                    setDeviceData(response.data.result);
                })
                .catch(error => {
                    setDeviceData([]);
                    const errorMsg = error?.response?.data?.message || 'Something went wrong!';
                    console.error('Error fetching data:', errorMsg);
                });
        }
    }

    const handleGradeChange = (e) => {
        const { name, value } = e.target;
        setStudentData((prevData) => {
            return {
                ...prevData,
                [name]: value,
                divisionId: ''
            }
        })
        const apiUrlForSyllabus = `/grade-wise-division?instituteMasterId=${studentData.instituteMasterId}&instituteGradeMasterId=${value}`;
        authorizedAxiosSection && authorizedAxiosSection.get(apiUrlForSyllabus)
            .then(response => {
                setDivisionList(response.data.result)
            })
            .catch(error => {
                const errorMsg = error?.response?.data?.message || 'Something went wrong!';
                console.error('Error fetching data:', errorMsg);
            });
    }

    const handleInputChange = (e) => {
        setStudentData((prevData) => {
            return {
                ...prevData,
                [e.target.name]: e.target.value
            }
        })
    }

    const handleInputReset = () => {
        setStudentData(initialForm);
        setSectionList([]);
        setYearList([]);
        setGradeList([]);
        setDivisionList([]);
        setDeviceData([]);
    }

    return (
        <>
            <Loader loading={loading} />
            <div className="wrapper d-flex flex-column min-vh-100 bg-light">
                <Header />
                <div className="container-fluid">
                    <nav aria-label="breadcrumb">
                        <ol className="breadcrumb my-0 ms-2">
                            <li className="breadcrumb-item dashboard" onClick={() => handleNavigate('/dashboard')}>
                                <span>Dashboard</span>
                            </li>
                            <li className="breadcrumb-item active"><span>Device Details</span></li>
                        </ol>
                    </nav>
                </div>
                <div className="container-lg">
                    <div className="row">
                        <div className="card border-info mx-auto mt-3">
                            <div className="card-content collpase show">
                                <div className="card-header" style={{ backgroundColor: '#3081D0', borderRadius: 0 }}>
                                    <h4 className="text-white"><b><span className="fa fa-file-o"></span><i class="	fas fa-mobile-alt"></i>Device Details</b></h4>
                                </div>
                                <div className="card-body">
                                    <div className="row">
                                        <div className="col-md-12">
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <label for="instituteMasterId" className="col-form-label"><span style={{ color: "red" }}>*</span>Select Institute</label>
                                                    <select class="form-select" aria-label="Default select example" name='instituteMasterId' onChange={handleInsituteChange} value={studentData.instituteMasterId} >
                                                        <option selected value={''} disabled>Select Institute</option>
                                                        {
                                                            instituteList?.map((institute) => {
                                                                return <option value={institute.id}>{institute.instituteName}</option>
                                                            })
                                                        }
                                                    </select>
                                                </div>
                                                <div className="col-md-6">
                                                    <label for="sectionMasterId" className="col-form-label"><span style={{ color: "red" }}>*</span>Select Section</label>
                                                    <select class="form-select" aria-label="Default select example" name='sectionMasterId' onChange={handleSectionChange} value={studentData.sectionMasterId} >
                                                        <option selected value={''} disabled>Select Section</option>
                                                        {
                                                            sectionList?.map((section) => {
                                                                return <option value={section.id}>{section.sectionName}</option>
                                                            })
                                                        }
                                                    </select>
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <label for="inputPassword" className="col-form-label">Select Year :</label>
                                                    <select class="form-select" aria-label="Default select example" name='year' onChange={handleYearChange} value={studentData.year}>
                                                        <option selected value={''} disabled>Select Year</option>
                                                        {
                                                            yearList?.map((year) => {
                                                                return <option value={year.academicYearMasterId}>{year.academicYearMasterDetails.yearName}</option>
                                                            })
                                                        }
                                                    </select>
                                                </div>
                                                <div className="col-md-6">
                                                    <label for="inputPassword" className="col-form-label">Select Class :</label>
                                                    <select class="form-select" aria-label="Default select example" name='gradeId' onChange={handleGradeChange} value={studentData.gradeId}>
                                                        <option selected value={''} disabled>Select Class</option>
                                                        {
                                                            gradeList?.map((grade) => {
                                                                return <option value={grade.id}>{grade.gradeName}</option>
                                                            })
                                                        }
                                                    </select>
                                                </div>
                                                <div className="col-md-6">
                                                    <label for="inputPassword" className="col-form-label">Select Division :</label>
                                                    <select class="form-select" aria-label="Default select example" name='divisionId' onChange={handleInputChange} value={studentData.divisionId}>
                                                        <option selected value={''} disabled>Select Division</option>
                                                        {
                                                            divisionList?.map((division) => {
                                                                return <option value={division.id}>{division.divisionName}</option>
                                                            })
                                                        }
                                                    </select>
                                                </div>
                                            </div>

                                            <div className="col-md-12 mt-3 mb-1" style={{ textAlign: 'center' }}>
                                                <div className="col-auto">
                                                    <button className="btn btn-outline-danger delete_icon" type="button" style={{ marginRight: '20px' }} onClick={handleInputReset}>Reset</button>
                                                </div>
                                            </div>

                                            {studentData.instituteMasterId && studentData.sectionMasterId && <div className="row">
                                                <div className="d-grid gap-2 mt-2">
                                                    <div class="tab-content rounded-bottom custom_table">
                                                        {displayedData.length > 0 && <GeneratePdf id="studentCredentitals" title={displayedData} headerTitle={"Student deviceData"} />}
                                                        <div className='search_btn_row'/* style={{ flexWrap: 'nowrap' }}*/>
                                                            <input
                                                                className="form-control mr-sm-2"
                                                                type="Search"
                                                                placeholder="Search"
                                                                value={searchValue}
                                                                onChange={handleEmptySearch}
                                                                onKeyDown={(event) => {
                                                                    if (event.key === 'Enter') {
                                                                        handleSearch();
                                                                    }
                                                                }}
                                                            />
                                                            <button className="btn btn-outline-primary" onClick={handleSearch}><i class="fa fa-search" aria-hidden="true"></i></button>
                                                        </div>
                                                        <div id="studentCredentitals">
                                                            <div class="tab-pane p-3 active preview" role="tabpanel" id="preview-1000">
                                                                <table class="table">
                                                                    <thead>
                                                                        <tr>
                                                                            <th scope="col" className='sl_no'>Sr. No.</th>
                                                                            <th scope="col">Student Name</th>
                                                                            <th scope="col">Year Name</th>
                                                                            <th scope="col">Class</th>
                                                                            <th scope="col">Division</th>
                                                                            <th scope="col">User Name</th>
                                                                            <th scope="col">IMEI Number</th>
                                                                            <th scope="col">Serial Number</th>
                                                                            <th scope="col">Meta Info</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {
                                                                            displayedData?.length > 0 ?
                                                                                displayedData?.map((cred, index) => {
                                                                                    return (
                                                                                        <tr>
                                                                                            <th scope="row" className='sl_no'>{startIndex + index + 1}</th>
                                                                                            <td>{cred.studentName}</td>
                                                                                            <td>{cred.academicYearMasterDetails.yearName}</td>
                                                                                            <td>{cred.instituteGradeMasterDetails.gradeName}</td>
                                                                                            <td>{cred.instituteDivisionMasterDetails.divisionName}</td>
                                                                                            <td>{cred.studentId}</td>
                                                                                            <td>{cred.deviceInfo.imei_no}</td>
                                                                                            <td>{cred.deviceInfo.serial_no}</td>
                                                                                            <td>
                                                                                                {
                                                                                                    cred.deviceInfo.meta_info &&
                                                                                                    Object.entries(cred.deviceInfo.meta_info).map(([key, value], i) => (
                                                                                                        <div key={i}>
                                                                                                            <strong>{key.charAt(0).toUpperCase() + key.slice(1)} →</strong> {value}
                                                                                                        </div>
                                                                                                    ))
                                                                                                }
                                                                                            </td>
                                                                                        </tr>
                                                                                    )
                                                                                })
                                                                                :
                                                                                (<tr>
                                                                                    <th colspan={"100%"} style={{ textAlign: 'center' }}>No Device Details Available</th>
                                                                                </tr>)
                                                                        }
                                                                    </tbody>
                                                                </table>
                                                                <Pagination currPage={currPage} setCurrPage={setCurrPage} totalPages={totalPages} itemsPerPage={itemsPerPage} setItemsPerPage={setItemsPerPage} totalItem={deviceData.length} />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </>
    )
};

export default DeviceDetails;
